import { Box, InputBase, styled, useTheme } from '@mui/material';
import { SearchNormal1 } from 'iconsax-react';
import { type FC, useCallback, useContext, useEffect, useRef } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import Enums from '@core/types/api/Enums';

import { GlobalSearchDropdown } from './GlobalSearchDropdown';
import { useGlobalSearch } from './useGlobalSearch';

const SearchWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  maxWidth: 320,
  height: 40,
  backgroundColor: theme.palette.SlabGray.Stroke,
  border: `1px solid ${theme.palette.SlabGray[400]}`,
  borderRadius: 8,
  padding: '0 12px',
  gap: 8,
  cursor: 'text',
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  fontSize: 14,
  color: theme.palette.SlabGray[800],
  '& .MuiInputBase-input': {
    padding: 0,
    '&::placeholder': {
      color: theme.palette.SlabGray[800],
      opacity: 1,
    },
  },
}));

export const GlobalSearchInput: FC = () => {
  const theme = useTheme();
  const { userInfo } = useContext(SlabContext);
  const tenantId = userInfo.tenant.id;
  const anchorRef = useRef<HTMLDivElement>(null);

  const hasCopilotChat = userInfo.featureFlags.some(
    (it) => it.name === Enums.FeatureFlagName.FeatureFlagCopilotChat && it.isEnabled,
  );

  const search = useGlobalSearch(tenantId);

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key !== '/') return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if ((e.target as HTMLElement)?.isContentEditable) return;
      e.preventDefault();
      search.inputRef.current?.focus();
    };
    document.addEventListener('keydown', handler);
    return (): void => {
      document.removeEventListener('keydown', handler);
    };
  }, []);

  const handleClose = useCallback(() => {
    search.close();
    search.inputRef.current?.blur();
  }, [search.close, search.inputRef]);

  return (
    <>
      <SearchWrapper
        ref={anchorRef}
        onClick={() => search.inputRef.current?.focus()}
        sx={{
          borderColor: search.isOpen ? theme.palette.SlabIndigo[500] : theme.palette.SlabGray[400],
        }}
      >
        <SearchNormal1 size={20} variant='TwoTone' color={theme.palette.SlabGray[800]} />
        <StyledInput
          inputRef={search.inputRef}
          placeholder={hasCopilotChat ? '/ to search or ask anything' : '/ to search'}
          value={search.query}
          onChange={(e) => search.setQuery(e.target.value)}
          onFocus={search.open}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              handleClose();
            }
          }}
        />
      </SearchWrapper>

      <GlobalSearchDropdown anchorEl={anchorRef.current} search={search} onClose={handleClose} />
    </>
  );
};
